import { closest } from 'next/javascripts/modules/dom';
import { getGroupItemPhysicalRows } from '../../helpers';

export function registerRowSelectPlugin(Handsontable) {
  const checkboxChangeListenerAdded = new WeakMap();

  class RowSelectPlugin extends Handsontable.plugins.BasePlugin {
    constructor(hotInstance) {
      super(hotInstance);

      this.selectedRows = [];

      this.addEventListeners();
      this.addHook('afterGetRowHeader', this.renderRowHeader.bind(this));
      this.addHook('afterRemoveRow', this.handleAfterRemoveRow.bind(this));
    }

    clearSelected() {
      this.toggleCheckboxesChecked(this.selectedRows.map(s => s.row), false);
      this.selectedRows = [];
    }

    renderRowHeader(row, th) {
      const physicalRow = this.hot.toPhysicalRow(row);
      const checked = this.selectedRows
        .findIndex(selected => selected.row === physicalRow) !== -1;
      const disabled = !this.hot.getDataAtRowProp(row, 'group')
        && !this.hot.getDataAtRowProp(row, 'id');

      Handsontable.dom.empty(th);
      th.insertAdjacentHTML('beforeend', `
        <div class="tr-checkbox-field">
          <div class="mdc-checkbox${disabled ? ' mdc-checkbox--disabled' : ''}">
            <input type="checkbox"
              class="row-select-checkbox mdc-checkbox__native-control"
              data-row="${physicalRow}"
              ${checked ? 'checked' : ''}${disabled ? ' disabled' : ''}>
            <div class="mdc-checkbox__background">
              <svg class="mdc-checkbox__checkmark" viewBox="0 0 24 24">
                <path class="mdc-checkbox__checkmark-path" fill="none"
                  d="M1.73,12.91 8.1,19.28 22.79,4.59"/>
              </svg>
            <div class="mdc-checkbox__mixedmark"></div>
          </div>
        </div>
      `);
    }

    handleAfterRemoveRow(index, amount, physicalRows) {
      if (this.selectedRows.length === 0) return;

      physicalRows.forEach((row) => {
        const selectedIndex = this.selectedRows.findIndex(s => s.row === row);
        if (selectedIndex === -1) return;

        this.selectedRows
          .filter(s => s.row > this.selectedRows[selectedIndex].row)
          .forEach((selected) => {
            // eslint-disable-next-line no-param-reassign
            selected.row -= 1;
          });

        const selected = this.selectedRows[selectedIndex];
        this.selectedRows.splice(selectedIndex, 1);
        this.unselectGroupRowIfNeeded(selected.groupRow);
      });
    }

    addEventListeners() {
      if (!checkboxChangeListenerAdded.has(this)) {
        this.checkboxChangeProxy = this.handleRowSelectCheckboxChange.bind(this);
        this.hot.rootElement.addEventListener('change', this.checkboxChangeProxy);
        checkboxChangeListenerAdded.set(this, true);
      }
    }

    handleRowSelectCheckboxChange(event) {
      const checkbox = closest(event.target, '.row-select-checkbox');
      if (checkbox) {
        event.stopImmediatePropagation();

        const { checked } = event.target;
        const visualRow = this.hot.toVisualRow(parseInt(event.target.dataset.row, 10));

        if (this.hot.getDataAtRowProp(visualRow, 'group')) {
          this.updateSelectedRowsWithGroup(visualRow, checked);
        } else {
          this.updateSelectedRows(visualRow, checked);
        }

        this.hot.runHooks('afterRowSelectChange', this.selectedRows.map(s => s.row));
      }
    }

    updateSelectedRowsWithGroup(row, checked) {
      const physicalRow = this.hot.toPhysicalRow(row);
      const groupItemPhysicalRows = getGroupItemPhysicalRows(this.hot, row);

      if (checked) {
        this.selectedRows.push({ row: physicalRow, groupRow: -1 });

        groupItemPhysicalRows.forEach((itemRow) => {
          if (!this.selectedRows.find(selected => selected.row === itemRow)) {
            this.selectedRows.push({ row: itemRow, groupRow: row });
          }
        });
      } else {
        this.selectedRows = this.selectedRows.filter(
          selected => selected.row !== physicalRow && selected.groupRow !== physicalRow,
        );
      }

      this.toggleCheckboxesChecked(groupItemPhysicalRows, checked);
    }

    updateSelectedRows(row, checked) {
      const physicalRow = this.hot.toPhysicalRow(row);

      if (checked) {
        this.selectedRows.push({
          row: physicalRow,
          groupRow: this.getGroupRow(physicalRow),
        });
      } else {
        const selectedIndex = this
          .selectedRows.findIndex(selected => selected.row === physicalRow);
        const selected = this.selectedRows[selectedIndex];

        this.selectedRows.splice(selectedIndex, 1);
        this.unselectGroupRowIfNeeded(selected.groupRow);
      }
    }

    unselectGroupRowIfNeeded(groupRow) {
      const unselectGroup = this.selectedRows
        .filter(s => s.groupRow === groupRow).length === 0;

      if (unselectGroup) {
        const selectedGroupIndex = this.selectedRows
          .findIndex(s => s.row === groupRow);
        if (selectedGroupIndex !== -1) {
          this.selectedRows.splice(selectedGroupIndex, 1);
          this.toggleCheckboxesChecked([groupRow], false);
        }
      }
    }

    toggleCheckboxesChecked(physicalRows, checked) {
      const selectors = physicalRows
        .map(row => `.row-select-checkbox[data-row="${row}"]`).join(',');
      if (!selectors) return;

      const table = this.hot.rootElement.querySelector('.ht_clone_left');
      [...table.querySelectorAll(selectors)].forEach((checkbox) => {
        // eslint-disable-next-line no-param-reassign
        checkbox.checked = checked;
      });
    }

    getGroupRow(physicalRow) {
      const sourceData = this.hot.getSourceData();
      if (sourceData.findIndex(data => data.group) === -1) return -1;

      const reverseGroupRow = sourceData.slice(0, physicalRow)
        .reverse().findIndex(data => data.group);
      return physicalRow - 1 - reverseGroupRow;
    }

    destroy() {
      this.selectedRows = [];

      if (checkboxChangeListenerAdded.has(this)) {
        this.hot.rootElement.removeEventListener('change', this.checkboxChangeProxy);
      }

      super.destroy();
    }
  }

  Handsontable.plugins.registerPlugin('rowSelect', RowSelectPlugin);
}
